<template>
  <div style="margin-bottom: var(--spacing-7)">
    <!-- Heading and informational text -->
    <patternlib-headline animation="never" separator="none" v-if="$t('title')">
      {{ $t('title') }}
    </patternlib-headline>
    <p style="margin-top: var(--spacing-3)">
      {{ $t('subtitle') }}
    </p>
    <p>
      <b>{{ $t('information') }}</b>
    </p>
    <!-- Form -->
    <div>
      <!-- Personal data -->
      <patternlib-headline
        style="margin-top: var(--spacing-7); margin-bottom: var(--spacing-4)"
        heading-tag="h6"
        :headline="$t('content.personal-data-title')"
      >
      </patternlib-headline>
      <div class="input-area">
        <patternlib-textinput
          :label="$t('content.prename')"
          :value="firstName || ''"
          @lhChange="firstName = $event.detail.value.toString()"
          :readonly="isLoading"
          v-rule="
            groupedValidationRule(
              firstName,
              'orderGuaranteeCreate',
              requiredRule
            )
          "
        >
        </patternlib-textinput>
        <patternlib-textinput
          :label="$t('content.name')"
          :value="lastName || ''"
          @lhChange="lastName = $event.detail.value.toString()"
          v-rule="
            groupedValidationRule(
              lastName,
              'orderGuaranteeCreate',
              requiredRule
            )
          "
          :readonly="isLoading"
        >
        </patternlib-textinput>
        <patternlib-textinput
          :label="$t('content.email')"
          type="email"
          :value="email"
          @lhChange="email = $event.detail.value.toString()"
          v-rule="
            groupedValidationRule(
              email,
              'orderGuaranteeCreate',
              requiredRule,
              emailRule
            )
          "
          :readonly="isLoading"
        >
        </patternlib-textinput>
        <!-- Fix: Set Form with autocomplete off for edge-chromium -->
        <form class="input-container" autocomplete="off">
          <patternlib-textinput
            :label="$t('content.repeat-email')"
            type="text"
            :value="validationMail"
            @lhChange="validationMail = $event.detail.value.toString()"
            onpaste="return false;"
            autocomplete="off"
            aria-autocomplete="none"
            list="off"
            v-rule="
              groupedValidationRule(
                [validationMail, email],
                'orderGuaranteeCreate',
                requiredRule,
                emailRepeatRule
              )
            "
            :readonly="isLoading"
          >
          </patternlib-textinput>
        </form>
      </div>
      <!-- Device data -->
      <patternlib-headline
        style="margin-top: var(--spacing-6); margin-bottom: var(--spacing-4)"
        heading-tag="h6"
        :headline="$t('content.device-data-title')"
      >
      </patternlib-headline>
      <div class="input-area">
        <div class="input-container">
          <div
            style="display: flex; flex-direction: row; align-items: baseline"
            id="custom-textinput"
          >
            <span class="label text-form-label pb-1">{{
              $t('content.serialnumber')
            }}</span>
            <div
              @click="openModal()"
              class="text-form-label pb-1 modal-button"
              style="
                flex-grow: 1;
                text-align: end;
                color: var(--color-steel-800);
              "
            >
              <span>{{ $t('content.serialnumber-helper') }}</span>
            </div>
          </div>
          <patternlib-textinput
            :value="serialnumberFormatted"
            :maxlength="12"
            @lhChange="
              serialnumber = formatInput($event.detail.value.toString())
            "
            :readonly="isLoading"
            :info-text="$t('content.information-serial-number')"
            v-rule="
              groupedValidationRule(
                serialnumber,
                'orderGuaranteeCreate',
                requiredRule,
                serialnumberRule
              )
            "
            @keydown="
              ($event.which < 48 || $event.which > 57) &&
              $event.which !== 9 &&
              $event.which !== 8 &&
              !($event.ctrlKey && $event.which === 86) &&
              !($event.ctrlKey && $event.which === 65) &&
              ($event.which < 96 || $event.which > 105)
                ? $event.preventDefault()
                : null
            "
          >
          </patternlib-textinput>
        </div>
        <patternlib-datepicker-single
          :label="$t('content.purchase-date')"
          :maximum="new Date().toISOString()"
          :displayed-month="new Date().toISOString()"
          :minimum="
            (() => {
              const date = new Date();
              date.setFullYear(new Date().getFullYear() - 2);
              const date2 = new Date('2024-01-01T00:00:00');
              if (date < date2) {
                return date2.toISOString();
              }
              return date.toISOString();
            })()
          "
          v-rule="
            groupedValidationRule(
              appliancePurchaseDate,
              'orderGuaranteeCreate',
              requiredRule
            )
          "
          :value="
            !!appliancePurchaseDate ? appliancePurchaseDate.toString() : ''
          "
          :date-time-format="locale || 'en_GB'"
          @lhChange="
            appliancePurchaseDate =
              'value' in $event.detail ? undefined : <Date>$event.detail
          "
          :disabled="isLoading"
        >
        </patternlib-datepicker-single>
        <p
          v-html="$t('content.dse-text')"
          style="margin-top: var(--spacing-2)"
        ></p>

        <div
          class="display: flex"
          style="margin-top: var(--spacing-5)"
          :style="
            breakpoint == 'isMobile'
              ? 'flex-direction: column'
              : 'flex-direction: row'
          "
        >
          <patternlib-button
            :display="breakpoint == 'isMobile' ? 'block' : 'inline-block'"
            class="responsiveButton"
            :label="$t('content.button-send-label')"
            :loading="isLoading"
            @click="!isLoading ? sendForm() : null"
          ></patternlib-button
          ><patternlib-button
            :display="breakpoint == 'isMobile' ? 'block' : 'inline-block'"
            class="responsiveButton"
            :label="$t('content.button-remove-label')"
            type="secondary"
            :style="
              breakpoint != 'isMobile'
                ? 'margin-left: var(--spacing-3)'
                : 'margin-top: var(--spacing-2)'
            "
            :disabled="isLoading"
            @click="resetForm"
          ></patternlib-button>
        </div>
      </div>
    </div>
    <patternlib-toast ref="toast" position="top-right"></patternlib-toast>
  </div>
</template>

<script setup lang="ts">
  import { useValidation } from '@liebherr/form-validator-vue';
  import axios from 'axios';
  import { computed, inject, ref, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { getLocalization } from '../../shared/localizationService';
  import { GuaranteeCheck } from '../api/hau-itde-guarantee-free-portal-api';
  import { guaranteeApi } from '../api/hauItdeGuaranteePortalApi';
  import { isLoading } from '../setting/axios.global';

  const firstName = ref('');
  const lastName = ref('');
  const email = ref('');
  const serialnumber = ref('');
  const appliancePurchaseDate = ref<Date | undefined>();
  const validationMail = ref('');

  const toast = ref<HTMLPatternlibToastElement>();

  const { t } = useI18n();
  const breakpoint: any = inject('breakpoint');
  watch(breakpoint, (val) => (breakpoint.value = val));

  const locale = getLocalization().value.Locale;
  //prefetch configuration => faster loading times
  const configurationAsync = guaranteeApi.v1GuaranteeConfigurationGet(
    locale,
    window.location.origin
  );

  function resetForm() {
    firstName.value = '';
    lastName.value = '';
    email.value = '';
    serialnumber.value = '';
    validationMail.value = '';
    appliancePurchaseDate.value = undefined;
  }

  const {
    requiredRule,
    emailRule,
    serialnumberRule,
    emailRepeatRule,
    validateGroup,
    groupedValidationRule,
    hasGroupError
  } = useValidation();

  const openModal = () => {
    window.lh.openPageModalv2('hau-modal-ratingplate');
  };

  const createToast = async (message: string) => {
    await toast.value?.createToastItem({
      message,
      showIcon: true,
      type: 'warning',
      showUndo: false,
      autoHide: true,
      delay: 6000
    });
  };

  const serialnumberFormatted = computed(() => {
    const inputText = serialnumber.value.replace(/[^0-9]/g, '').slice(0, 9);
    const formattedValue = inputText.replace(
      /(\d{2})(\d{0,3})(\d{0,3})(\d?)/,
      (_match, p1, p2, p3, p4) => {
        let result = p1;
        if (p2) result += '.' + p2;
        if (p3) result += '.' + p3;
        if (p4) result += '.' + p4;
        return result;
      }
    );
    return formattedValue;
  });

  const formatInput = (value: string) => {
    return value.replace(/[^0-9]/g, '').slice(0, 9);
  };

  const sendForm = async () => {
    await validateGroup('orderGuaranteeCreate');
    if (hasGroupError('orderGuaranteeCreate')) {
      return;
    }

    try {
      //STEP 1: Check valid device
      try {
        await guaranteeApi.v1GuaranteeValidPost({
          serialNumber: serialnumber.value,
          locale: locale.replace('-', '_')
        } as GuaranteeCheck);
      } catch (error) {
        if (axios.isAxiosError(error) && error.status === 400) {
          return createToast(t('errors.invalid-device'));
        }

        throw error;
      }

      //STEP 2: Create Entry
      //--take day of locale date instead of utc date used by stringify
      var localeDate = appliancePurchaseDate.value
        ? new Date(
            appliancePurchaseDate.value.getTime() -
              appliancePurchaseDate.value.getTimezoneOffset() * 60000
          )
        : null;

      const result2 = await guaranteeApi.v1GuaranteePost({
        serialNumber: serialnumber.value,
        email: email.value,
        appliancePurchaseDate: localeDate ? localeDate.toISOString() : '',
        locale: locale.replace('-', '_'),
        firstName: firstName.value,
        lastname: lastName.value,
        type: 'FREE',
        initiator: 'WEBSITE'
      });

      if (result2.status === 200) {
        try {
          const response = await configurationAsync;

          if (response.status === 200 && response.data && response.data.url) {
            window.location.href =
              window.location.origin.toString() + response.data.url.toString();
          }
        } catch (error) {
          throw error;
        }
      }
    } catch (error: any) {
      if (axios.isAxiosError(error) && error.status === 409) {
        return createToast(t('errors.warranty-exists'));
      }
      return createToast(t('errors.unexpected-error'));
    }
  };
</script>

<style scoped>
  patternlib-textinput {
    margin-bottom: 5px;
  }

  .pb-1 {
    padding-bottom: var(--spacing-1);
  }
  .label {
    font-family: var(--form-label-font-family);
  }
  .text-form-label {
    font-size: var(--form-size-label-text);
  }
  .modal-button {
    color: var(--color-steel-600);
    text-decoration: underline;
  }
  .modal-button > span {
    cursor: pointer;
  }

  .input-area {
    gap: var(--spacing-2);
    display: flex;
    flex-direction: column;
  }
</style>
